import { useInitialMonth } from './usePinnedOrderMonth.js';
import { Reconciliations } from './Reconciliations.js';
import { contractCardStatus } from './contract-card-status.js';
import { ContractCreateButton } from '../components/ContractCreate.js';
/**
 * Раздел «Документы»: счета и акты, договоры, шаблоны, отчёты.
 *
 * Список счетов листается по месяцам и сортируется по ДАТЕ, а не по номеру
 * (§2.3): номера повторяются между годами, и только дата задаёт однозначный
 * порядок. При равной дате новее считается больший номер.
 */

import { Fragment, useMemo, useRef, useState, type ReactNode } from 'react';
import {
  ArrowDown, ArrowUp, Check, ChevronDown, ChevronLeft, ChevronRight, ChevronUp, CreditCard,
  Download, Eye, FileSignature, FileText, Mail,
  Paperclip, Pencil, RotateCcw, Search, Trash2, X,
} from 'lucide-react';
import {
  formatDate,
  formatKop,
  formatMonthKey,
  monthKeyForOffset,
  reportDate,
  todayIso,
  legalTitleText,
  DEFAULT_INVOICE_LETTER,
  fillLetter,
  LETTER_PLACEHOLDERS,
  needsContract,
  needsEmail,
  pluralRu,  invoiceNumberText,
} from '@otc/shared';

import { api, documentUrl, downloadInvoiceArchive, reportExportUrl } from '../api/client.js';
import {
  useBootstrap,
  useInvoices,
  usePriceLists,
  useReports,
  type ApiInvoice,
  type ApiPriceList,
  type ApiReport,
  type ApiReportRow,
} from '../api/queries.js';
import { useDrafts } from '../store/drafts.js';
import { useModule } from '../store/useModule.js';
import {
  Badge,
  Button,
  EmptyState,
  Modal,
  SectionHeader,
  SelectionActionButton,
  Spinner,
  Toast,
  useToast,
} from '../components/ui.js';
import { Card, CardFact, CardGrid } from '../components/Cards.js';
import { ConfirmDialog } from '../components/ConfirmDialog.js';
import { SaveDiscard } from '../components/SaveDiscard.js';
import { DocumentLinks } from '../components/DocumentLinks.js';
import { MultiSelectFilter } from '../components/MultiSelectFilter.js';
import { InvoiceDetailModal } from './InvoiceDetailModal.js';
import { ReportViewModal } from './ReportViewModal.js';
import { PriceListModal } from '../components/PriceListModal.js';
import { RequisitesTab } from './RequisitesTab.js';
import { useSearchParams } from 'react-router-dom';
import { STATUS_COLORS } from '../theme.js';
import { подсказкаПоиска } from '../components/isNarrow.js';
import { useFillHeight } from '../components/useFillHeight.js';
import { useTableZoom } from '../components/useTableZoom.js';
import { ZoomControl } from '../components/ZoomControl.js';

type Tab = 'invoices' | 'reconciliations' | 'contracts' | 'templates' | 'reports' | 'prices' | 'requisites';

const TABS: { id: Tab; label: string }[] = [
  { id: 'invoices', label: 'Счета и акты' },
  { id: 'reconciliations', label: 'Акты сверки' },
  { id: 'contracts', label: 'Договоры' },
  { id: 'reports', label: 'Отчёты' },
  { id: 'prices', label: 'Прайс-листы' },
  // Реквизиты и шаблоны последними: в них заходят при настройке, а не каждый день.
  { id: 'requisites', label: 'Реквизиты' },
  { id: 'templates', label: 'Шаблоны' },
];

export function Documents() {
  /*
   * Вкладку можно открыть ссылкой: «Настройки» ведут сюда за реквизитами,
   * а подсказки в документах — туда, где их заполнить.
   */
  const [params] = useSearchParams();
  const [tab, setTab] = useState<Tab>(() => (TABS.find((t) => t.id === params.get('tab'))?.id ?? 'invoices'));
  const { toast, show } = useToast();

  /* Листается содержимое вкладки, а не страница: название раздела и сами
     вкладки остаются на месте. Подробности — в `useFillHeight`. */
  const { ref: содержимоеRef, высота: высотаСодержимого } = useFillHeight();

  /* Масштаб содержимого вкладок: у каждого раздела свой. */
  const zoom = useTableZoom('documents', true);

  return (
    <div>
      {/*
        ПОДПИСИ У РАЗДЕЛА НЕТ.

        Она перечисляла то, что и так написано на вкладках прямо под ней:
        «Счета и акты», «Договоры», «Отчёты». На телефоне перечисление
        занимало две строки — полтора счёта из списка — и не сообщало ничего
        нового. В других разделах подпись остаётся: там в ней живые числа
        («56 врачей в базе»), а не пересказ вкладок.
      */}
      {/* Масштаб — единственная кнопка в шапке раздела: всё остальное
          управление живёт внутри вкладок и у каждой своё. */}
      {/*
        Масштаб В ШАПКЕ — только для вкладок без своего ряда отборов.

        В счетах он стоит внизу, при самом списке, как в «Заказах»: там
        управление размером живёт в одной полосе с отборами, а не наверху
        страницы, где до него ещё надо доехать глазами.
      */}
      <SectionHeader
        icon={FileText}
        title="Документы"
        right={
          tab === 'invoices' ? undefined : (
            <ZoomControl zoom={zoom} hint="Мельче — на экран влезет больше строк" />
          )
        }
      />

      {/*
        На ТЕЛЕФОНЕ разделы стоят СЕТКОЙ, а не полосой.

        Шесть разделов — это 636 точек подписей, а экран телефона даёт 370:
        полоса прокручивалась вбок, и половина разделов пряталась за краем.
        Понять, что там что-то есть, было не по чему — «Реквизиты» и
        «Шаблоны» существовали, но на телефоне их не находили.

        В два столбца помещаются все шесть сразу: раздел виден и открывается
        одним касанием. На широком экране остаётся привычная полоса.
      */}
      <div className="mb-5">
        <div
          className="grid grid-cols-2 gap-1 rounded-lg p-1 md:flex md:w-fit"
          style={{ background: 'var(--otc-surface)', border: '1px solid var(--otc-line)' }}
        >
          {TABS.map((item) => (
            <button
              key={item.id}
              onClick={() => setTab(item.id)}
              className="whitespace-nowrap rounded-md px-3.5 py-2 text-sm md:py-1.5"
              style={{
                background: tab === item.id ? 'var(--otc-accent-dark)' : 'transparent',
                color: tab === item.id ? '#fff' : 'var(--otc-ink2)',
              }}
            >
              {item.label}
            </button>
          ))}
        </div>
      </div>

      {/*
        Прокручивается СОДЕРЖИМОЕ ВКЛАДКИ. Счетов за год под тысячу, и,
        долистав до июньских, вернуться к другой вкладке можно было только
        прокруткой обратно наверх.
      */}
      <div
        ref={содержимоеRef}
        className="-mx-1 px-1"
        style={{ maxHeight: высотаСодержимого ?? undefined, overflowY: высотаСодержимого ? 'auto' : undefined }}
      >
        {/*
          Масштаб передаётся ВНУТРЬ вкладки, а не накрывает её целиком.

          Общей обёрткой он жал всё подряд: на 80 % мельчали и строки, и
          поиск, и кнопки месяцев — в «Заказах» же уменьшается только таблица,
          а органы управления остаются прежними, чтобы в них можно было
          попадать мышью при любом размере. Каждая вкладка применяет масштаб
          сама — ровно к своему списку.
        */}
        {tab === 'invoices' && <InvoicesTab show={show} zoom={zoom} />}
        {tab === 'reconciliations' && <Reconciliations />}
        {tab === 'contracts' && <ContractsTab show={show} масштаб={zoom.zoom} />}
        {tab === 'templates' && <TemplatesTab show={show} />}
        {tab === 'requisites' && <RequisitesTab show={show} />}
        {tab === 'reports' && <ReportsTab show={show} масштаб={zoom.zoom} />}
        {tab === 'prices' && <PriceListsTab show={show} масштаб={zoom.zoom} />}
      </div>

      <Toast toast={toast} />
    </div>
  );
}

// ---------------------------------------------------------------------------

/**
 * Порядок в списке счетов.
 *
 * `sum` — от большей суммы к меньшей, `sumAsc` — наоборот. Отбор «По имени»
 * убран владельцем 28.09.2026: юрлиц в месяце единицы, и для них есть свой
 * отбор — раскладывать по алфавиту было незачем.
 */
type InvoiceSort = 'new' | 'old' | 'sum' | 'sumAsc';

/**
 * Кнопка порядка — «По сумме», «По имени».
 *
 * Выглядит как плашка отбора рядом: это тоже вопрос к списку, просто не
 * «какие показать», а «как разложить». Включённая подсвечивается акцентом,
 * как отбор с выбранным значением.
 */
function ПорядокСчетов({
  label,
  направление,
  onClick,
  title,
}: {
  label: string;
  /** `null` — выключено, иначе стрелка показывает, в какую сторону разложено. */
  направление: 'вниз' | 'вверх' | null;
  onClick: () => void;
  title: string;
}) {
  const active = направление !== null;
  return (
    <button
      onClick={onClick}
      aria-pressed={active}
      title={title}
      className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-md border px-2.5 py-1.5 text-xs"
      style={{
        borderColor: active ? 'var(--otc-accent)' : 'var(--otc-line)',
        color: active ? 'var(--otc-accent)' : 'var(--otc-ink2)',
        background: active
          ? 'color-mix(in srgb, var(--otc-accent) 10%, var(--otc-surface))'
          : 'var(--otc-surface)',
      }}
    >
      {label}
      {/* Стрелка показывает сторону: вниз — от большего к меньшему. */}
      {направление === 'вниз' && <ArrowDown size={13} className="flex-shrink-0" />}
      {направление === 'вверх' && <ArrowUp size={13} className="flex-shrink-0" />}
    </button>
  );
}

/**
 * Месяцы полосы отбора.
 *
 * На широком экране — ПОЛНЫМИ названиями: место есть, а «янв/фев/мар» надо
 * мысленно разворачивать, да и «мар» с «май» на беглый взгляд похожи.
 * Сокращения остаются на телефоне: там двенадцать полных названий заняли бы
 * пол-экрана.
 */
const MONTHS_SHORT = [
  'янв', 'фев', 'мар', 'апр', 'май', 'июн',
  'июл', 'авг', 'сен', 'окт', 'ноя', 'дек',
];

const MONTHS_FULL = [
  'январь', 'февраль', 'март', 'апрель', 'май', 'июнь',
  'июль', 'август', 'сентябрь', 'октябрь', 'ноябрь', 'декабрь',
];

const invoiceTotalKop = (invoice: ApiInvoice): number =>
  invoice.items.reduce((sum, item) => sum + Math.round(item.priceKop * item.qty), 0);

function InvoicesTab({
  show,
  zoom,
}: {
  show: (text: string, tone?: 'info' | 'error') => void;
  /**
   * Масштаб СТРОК списка — и сам переключатель, который стоит в ряду отборов.
   *
   * Управление размером живёт при списке, а не в шапке страницы: так же
   * устроен журнал заказов, и уменьшается только список, а отборы, месяцы
   * и кнопки остаются прежними — в них надо попадать мышью при любом размере.
   */
  zoom: ReturnType<typeof useTableZoom>;
}) {
  const масштаб = zoom.zoom;
  const today = todayIso();
  const initialMonth = useInitialMonth(today);
  const [openId, setOpenId] = useState<string | null>(null);
  const [confirmInvoice, setConfirmInvoice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  /*
    Отбор: год, месяц, юрлицо, статус.

    Раньше по списку ходили стрелками по месяцам — с января на ноябрь десять
    нажатий, а найти все счета одной клиники за год было нельзя вовсе.
    Год отдельно от месяца потому, что нумерация счетов с него начинается
    заново, и «№12» без года ничего не значит.
  */
  const [year, setYear] = useState(() => Number(initialMonth.slice(0, 4)));
  /** `null` — весь год. */
  const [month, setMonth] = useState<number | null>(() => Number(initialMonth.slice(5, 7)));
  const [legalIds, setLegalIds] = useState<string[]>([]);
  /*
    Оплата и отправка — ДВА РАЗНЫХ отбора, а не один список из четырёх меток.

    В общем списке они складывались в одном меню, и «Отправлен» с «Оплачен»
    читались как варианты одного вопроса, хотя это разные вопросы к счёту:
    ушло ли письмо и пришли ли деньги. Разделив, мы заодно избавились от
    разбора меток по строкам при отборе.
  */
  const [paidFilter, setPaidFilter] = useState<string[]>([]);
  const [sentFilter, setSentFilter] = useState<string[]>([]);
  const [sort, setSort] = useState<InvoiceSort>('new');
  const [filtersOpen, setFiltersOpen] = useState(false);
  /** Поиск по номеру счёта, клинике или юрлицу. Отменяет отбор по периоду. */
  const [query, setQuery] = useState('');

  /** Отмеченные счета. Панель действий появляется, как только выбран один. */
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [confirmBulk, setConfirmBulk] = useState(false);
  /** Подтверждение массовой отметки оплаты: деньги — не то, что ставят мимоходом. */
  const [confirmPaid, setConfirmPaid] = useState(false);
  const [archiving, setArchiving] = useState<'docx' | 'pdf' | null>(null);

  const bootstrap = useBootstrap(true);
  const invoices = useInvoices(true);

  const all = useMemo(() => invoices.data?.invoices ?? [], [invoices.data]);

  /** Годы, в которых вообще есть счета, плюс текущий — чтобы было куда встать. */
  const years = useMemo(() => {
    const set = new Set(all.map((i) => Number(i.date.slice(0, 4))));
    set.add(Number(today.slice(0, 4)));
    return [...set].sort((a, b) => b - a);
  }, [all, today]);

  /*
   * Соседние годы — ТОЛЬКО ИЗ ТЕХ, ГДЕ ЕСТЬ СЧЕТА.
   *
   * Стрелки просто прибавляли и убавляли единицу и с любого края уводили в
   * пустоту: 2024, 2023, 2022 — годы, которых у лаборатории в программе нет
   * вовсе. Человек видел «Нет счетов и актов по заданным условиям» и не мог
   * понять, то ли данные пропали, то ли он сам куда-то забрёл.
   *
   * `years` отсортирован по убыванию, поэтому предыдущий год лежит ДАЛЬШЕ по
   * списку, а следующий — ближе к началу. На краю соседа нет, и стрелка
   * гаснет: ходить больше некуда.
   */
  const yearIndex = years.indexOf(year);
  const prevYear = yearIndex >= 0 ? years[yearIndex + 1] : undefined;
  const nextYear = yearIndex >= 0 ? years[yearIndex - 1] : undefined;

  /*
   * Смена года СБРАСЫВАЕТ месяц на «весь год».
   *
   * Месяц оставался прежним, и переход в другой год почти всегда упирался в
   * пустоту: в сентябре прошлого года счетов могло не быть вовсе, а на экране
   * было ровно то же «нет счетов», что и при поломке. Другой год смотрят
   * целиком, а месяц в нём выбирают уже потом — по тому, что там есть.
   */
  const выбратьГод = (следующий: number) => {
    setYear(следующий);
    setMonth(null);
  };

  /**
   * Юрлица В ВЫБРАННОМ ПЕРИОДЕ, а не за всё время.
   *
   * Список из 37 юрлиц не нужен и сам по себе, но хуже другое: в нём стояли
   * те, кому в этом месяце счетов не выставляли вовсе. Выбираешь такое — и
   * получаешь пустой экран, а понять, дело в месяце или в юрлице, нельзя.
   * Теперь в списке ровно те, по кому здесь есть что показать.
   *
   * Уже выбранное юрлицо остаётся в списке, даже если в новом месяце счетов
   * по нему нет: иначе отбор действовал бы, а снять его в меню было бы
   * нечем — строка оттуда пропала.
   */
  const legalOptions = useMemo(() => {
    const map = new Map<string, string>();
    const подпись = (i: ApiInvoice) =>
      legalTitleText({ shortForm: i.legalShortForm, name: i.legalName });

    for (const invoice of all) {
      const вПериоде =
        Number(invoice.date.slice(0, 4)) === year &&
        (month === null || Number(invoice.date.slice(5, 7)) === month);
      if (вПериоде || legalIds.includes(invoice.legalId)) map.set(invoice.legalId, подпись(invoice));
    }

    return [...map].map(([value, label]) => ({ value, label })).sort((a, b) => a.label.localeCompare(b.label, 'ru'));
  }, [all, year, month, legalIds]);

  const visible = useMemo(() => {
    const запрос = query.trim().toLowerCase().replace(/^№\s*/, '');

    const filtered = all.filter((invoice) => {
      /*
        ПОИСК ИДЁТ ВНУТРИ ВЫБРАННОГО ПЕРИОДА (решение владельца 28.09.2026).

        Прежде он отменял отбор по году и месяцу и искал по всему архиву.
        Замысел был в том, что номер ищут, когда не помнят месяца, — но на
        деле выходило хуже: выбран сентябрь, человек ищет «344», а находится
        прошлогодний счёт с тем же номером, потому что нумерация годовая.
        Теперь область поиска ровно та, что на экране: месяц или, если выбран
        «Весь год», весь год. Чтобы искать шире, достаточно нажать «Весь год».
      */
      if (Number(invoice.date.slice(0, 4)) !== year) return false;
      if (month !== null && Number(invoice.date.slice(5, 7)) !== month) return false;

      if (запрос) {
        const номер = invoiceNumberText(invoice.number, invoice.numberSuffix).toLowerCase();
        const совпало =
          номер.startsWith(запрос) ||
          invoice.clinicName.toLowerCase().includes(запрос) ||
          invoice.legalName.toLowerCase().includes(запрос);
        if (!совпало) return false;
      }
      if (legalIds.length > 0 && !legalIds.includes(invoice.legalId)) return false;
      /*
        Отправка и оплата отбираются НЕЗАВИСИМО.

        Раньше в отборе стоял сводный статус, и «Отправлен» не находил
        отправленные счета, которые успели оплатить: у них сводный статус
        становился «Оплачено», и по нему они выпадали из выборки. Теперь
        отмеченные метки складываются по «или» внутри каждого вопроса и по
        «и» между вопросами: «Отправлен» + «Не оплачен» — это ровно те
        счета, по которым ждут денег.
      */
      if (paidFilter.length && !paidFilter.includes(invoice.paid ? 'Оплачен' : 'Не оплачен')) return false;
      if (sentFilter.length && !sentFilter.includes(invoice.sent ? 'Отправлен' : 'Не отправлен')) return false;
      return true;
    });

    // При равной дате новее тот, у кого больше номер (§2.3).
    const byDate = (a: ApiInvoice, b: ApiInvoice) =>
      a.date === b.date ? b.number - a.number : b.date.localeCompare(a.date);

    return [...filtered].sort((a, b) => {
      if (sort === 'old') return -byDate(a, b);
      if (sort === 'sum') return invoiceTotalKop(b) - invoiceTotalKop(a);
      if (sort === 'sumAsc') return invoiceTotalKop(a) - invoiceTotalKop(b);
      return byDate(a, b);
    });
  }, [all, year, month, legalIds, paidFilter, sentFilter, sort, query]);

  /*
    Из выбора выпадают счета, скрытые отбором: иначе «удалить 3» тронуло бы
    записи, которых на экране нет, — и человек не увидел бы, что именно ушло.
  */
  const chosen = useMemo(
    () => visible.filter((invoice) => selected.has(invoice.id)),
    [visible, selected],
  );

  /** Из отмеченных — те, что ещё не оплачены: только их и трогает отметка. */
  const ждутОплаты = useMemo(() => chosen.filter((invoice) => !invoice.paid), [chosen]);

  const shownSumKop = visible.reduce((sum, i) => sum + invoiceTotalKop(i), 0);
  const unpaidSumKop = visible
    .filter((i) => !i.paid)
    .reduce((sum, i) => sum + invoiceTotalKop(i), 0);

  const filtersActive =
    legalIds.length + paidFilter.length + sentFilter.length + (query.trim() ? 1 : 0);

  const openInvoice = visible.find((i) => i.id === openId) ?? null;
  const invoiceToDelete = visible.find((i) => i.id === confirmInvoice) ?? null;

  /**
   * Открыть счёт двойным щелчком, повторный по ТОМУ ЖЕ — закрыть.
   *
   * Открывают и закрывают одним движением, как ящик: ткнул ещё раз и убрал.
   * Ровно так же устроен журнал заказов, и разводить две карточки в разные
   * повадки незачем.
   */
  const открытьСчёт = (id: string) => setOpenId((текущий) => (текущий === id ? null : id));

  const toggle = (id: string) =>
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const removeInvoice = async (invoice: ApiInvoice) => {
    setBusy(true);
    try {
      const result = await api.del<{ releasedOrders: number }>(`/api/invoices/${invoice.id}`);
      show(`Счёт №${invoiceNumberText(invoice.number, invoice.numberSuffix)} удалён, заказов освобождено: ${result.releasedOrders}`);
      setConfirmInvoice(null);
      setSelected((current) => {
        const next = new Set(current);
        next.delete(invoice.id);
        return next;
      });
      void invoices.refetch();
    } catch (error) {
      show(error instanceof Error ? error.message : 'Не удалось удалить', 'error');
    } finally {
      setBusy(false);
    }
  };

  /**
   * Удаление нескольких счетов.
   *
   * Последовательно, а не пачкой: каждый счёт освобождает свои заказы, и при
   * отказе на середине должно быть видно, что уже удалено, а что осталось.
   */
  const removeChosen = async () => {
    setBusy(true);
    let removed = 0;
    let released = 0;
    try {
      for (const invoice of chosen) {
        const result = await api.del<{ releasedOrders: number }>(`/api/invoices/${invoice.id}`);
        removed += 1;
        released += result.releasedOrders;
      }
      show(
        `Удалено ${removed} ${pluralRu(removed, ['счёт', 'счёта', 'счетов'])}, ` +
          `заказов освобождено: ${released}`,
      );
    } catch (error) {
      const текст = error instanceof Error ? error.message : 'Не удалось удалить';
      show(removed > 0 ? `Удалено ${removed}, дальше остановились: ${текст}` : текст, 'error');
    } finally {
      setConfirmBulk(false);
      setSelected(new Set());
      setBusy(false);
      void invoices.refetch();
    }
  };

  /**
   * ОТМЕТИТЬ ОПЛАЧЕННЫМИ несколько счетов разом.
   *
   * Деньги приходят платёжкой сразу за месяц — по пять-десять счетов одной
   * клиники, — и отмечать их приходилось по одному: открыть счёт, нажать
   * «Оплачен», закрыть, открыть следующий. То же самое в заказах делается
   * полосой выделения одним нажатием.
   *
   * Последовательно, а не пачкой: сервер по каждому счёту метит ещё и его
   * заказы, и при отказе на середине должно быть видно, что уже прошло.
   *
   * Уже оплаченные пропускаются молча: в выборку они попадают заодно, когда
   * берут «выбрать все», и отказываться из-за них — значит заставить человека
   * вычищать выделение вручную.
   */
  const отметитьОплаченными = async () => {
    const ждут = chosen.filter((invoice) => !invoice.paid);
    if (ждут.length === 0) {
      show('Все выбранные счета уже оплачены');
      return;
    }
    setBusy(true);
    let отмечено = 0;
    try {
      for (const invoice of ждут) {
        await api.post(`/api/invoices/${invoice.id}/paid`, { paid: true });
        отмечено += 1;
      }
      show(
        `Оплачено ${отмечено} ${pluralRu(отмечено, ['счёт', 'счёта', 'счетов'])} — ` +
          'вместе с их заказами',
      );
    } catch (error) {
      const текст = error instanceof Error ? error.message : 'Не удалось отметить оплату';
      show(отмечено > 0 ? `Отмечено ${отмечено}, дальше остановились: ${текст}` : текст, 'error');
    } finally {
      setConfirmPaid(false);
      setSelected(new Set());
      setBusy(false);
      void invoices.refetch();
    }
  };

  /**
   * Счётчик выбранного с суммой — общий для телефона и широкого экрана.
   *
   * Одного числа счетов мало: перед «Оплачено» первым делом смотрят на
   * сумму, иначе приходится закрывать панель и складывать строки глазами.
   */
  const счётчикВыбранного = (
    <span className="flex items-baseline gap-1.5 whitespace-nowrap">
      <span className="tabular text-base font-semibold leading-none text-ink">{chosen.length}</span>
      <span className="text-[11px] text-ink2">выбрано</span>
      <span className="tabular text-[11px] font-medium text-ink">
        {formatKop(chosen.reduce((sum, i) => sum + invoiceTotalKop(i), 0))} ₽
      </span>
    </span>
  );

  /**
   * Снять выделение — КРЕСТИКОМ, а не словом «Снять».
   *
   * Слово занимало место подписи в ряду значков и читалось как ещё одно
   * действие над счетами, вроде «снять оплату». Крестик означает ровно
   * «закрыть эту панель» и ни с чем не путается — он же стоит в «Заказах».
   */
  const кнопкаСнять = (
    <button
      onClick={() => setSelected(new Set())}
      className="flex-shrink-0 rounded-full p-2 hover:bg-black/5"
      title="Снять выделение"
      aria-label="Снять выделение"
    >
      <X size={15} className="text-ink2" />
    </button>
  );

  const grabArchive = async (format: 'docx' | 'pdf') => {
    setArchiving(format);
    try {
      await downloadInvoiceArchive(chosen.map((i) => i.id), format);
    } catch (error) {
      show(error instanceof Error ? error.message : 'Не удалось собрать архив', 'error');
    } finally {
      setArchiving(null);
    }
  };

  if (invoices.isLoading || bootstrap.isLoading || !bootstrap.data) return <Spinner />;

  return (
    <div>
      {/*
        Отбор: год, месяц, юрлицо, статус.

        На телефоне всё, кроме года, прячется под кнопкой «Фильтры»: строка
        отбора там заняла бы полэкрана, а ходят по ней не каждый раз.
      */}
      <div className="mb-2 flex flex-wrap items-center gap-2">
        <button
          onClick={() => prevYear !== undefined && выбратьГод(prevYear)}
          disabled={prevYear === undefined}
          className="otc-btn otc-btn-secondary otc-btn-icon"
          style={{ borderColor: 'var(--otc-line)' }}
          aria-label="Предыдущий год"
          title={prevYear === undefined ? 'Раньше счетов нет' : 'Перейти в ' + prevYear + ' год'}
        >
          <ChevronLeft size={15} className="text-ink2" />
        </button>
        {/* Единственный год — выбирать не из чего: список раскрылся бы одной
            строкой. Бледнеть при этом он не должен, поэтому непрозрачность
            возвращается: это не отключённое поле, а просто нечего выбирать. */}
        <select
          className="rounded-lg border px-2 py-1 text-sm font-medium disabled:opacity-100"
          style={{ borderColor: 'var(--otc-line)', background: 'var(--otc-surface)', color: 'var(--otc-ink)' }}
          disabled={years.length < 2}
          value={year}
          onChange={(e) => выбратьГод(Number(e.target.value))}
          aria-label="Год"
        >
          {years.map((y) => (
            <option key={y} value={y}>{y} г.</option>
          ))}
        </select>
        <button
          onClick={() => nextYear !== undefined && выбратьГод(nextYear)}
          disabled={nextYear === undefined}
          className="otc-btn otc-btn-secondary otc-btn-icon"
          style={{ borderColor: 'var(--otc-line)' }}
          aria-label="Следующий год"
          title={nextYear === undefined ? 'Позже счетов нет' : 'Перейти в ' + nextYear + ' год'}
        >
          <ChevronRight size={15} className="text-ink2" />
        </button>

        <button
          onClick={() => setFiltersOpen((v) => !v)}
          className="inline-flex items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-[13px] text-ink2 md:hidden"
          style={{ borderColor: 'var(--otc-line)', background: 'var(--otc-surface)' }}
        >
          Фильтры
          {filtersActive > 0 && (
            <span
              className="rounded-full px-1.5 text-[11px] font-medium text-white"
              style={{ background: 'var(--otc-accent)' }}
            >
              {filtersActive}
            </span>
          )}
        </button>

      </div>

      {/*
        Подсказки «ищем только в выбранном месяце» здесь НЕТ (убрана
        владельцем 28.09.2026): область поиска и так видна — она написана
        на кнопках месяцев прямо над полем. Чтобы искать шире, нажимают
        «Весь год».
      */}

      {/* Месяцы — двенадцать кнопок: с января на ноябрь одним нажатием. */}
      <div className={`mb-2 flex-wrap items-center gap-1 md:flex ${filtersOpen ? 'flex' : 'hidden'}`}>
        {/* «Весь год» — той же породы, что месяцы: отдельное выделение его
            только выпячивало (убрано владельцем 28.09.2026). */}
        <button
          onClick={() => setMonth(null)}
          className="rounded-full border px-2.5 py-0.5 text-xs md:px-3.5 md:py-1.5 md:text-sm"
          style={{
            borderColor: month === null ? 'var(--otc-accent)' : 'var(--otc-line)',
            color: month === null ? 'var(--otc-accent)' : 'var(--otc-ink2)',
            /*
              Фон БЕЛЫЙ, как у полей и кнопок рядом.

              Месяцы были прозрачными и лежали прямо на фоне страницы, а стоящие
              под ними поиск, «Все юрлица» и стрелки года — на белом. В одном
              ряду это читалось как две разные породы кнопок: будто месяцы —
              подписи, а не то, что можно нажать.
            */
            background:
              month === null
                ? 'color-mix(in srgb, var(--otc-accent) 12%, var(--otc-surface))'
                : 'var(--otc-surface)',
          }}
        >
          Весь год
        </button>
        {MONTHS_SHORT.map((label, index) => {
          const value = index + 1;
          const active = month === value;
          return (
            <button
              key={label}
              onClick={() => setMonth(value)}
              className="rounded-full border px-2 py-0.5 text-xs md:px-3.5 md:py-1.5 md:text-sm"
              style={{
                borderColor: active ? 'var(--otc-accent)' : 'var(--otc-line)',
                color: active ? 'var(--otc-accent)' : 'var(--otc-ink2)',
                background: active
                  ? 'color-mix(in srgb, var(--otc-accent) 12%, var(--otc-surface))'
                  : 'var(--otc-surface)',
              }}
            >
              <span className="md:hidden">{label}</span>
              <span className="hidden md:inline">{MONTHS_FULL[index]}</span>
            </button>
          );
        })}
      </div>

      {/*
        Отборы — В БЕЛОЙ ПАНЕЛИ, как в «Заказах» (решение владельца 28.09.2026).

        Здесь они лежали прямо на фоне страницы и рассыпались среди месяцев,
        итогов и списка: было неясно, где кончается один ряд и начинается
        другой. В журнале заказов та же полоса собрана в белую карточку
        с рамкой, и разводить два одинаковых по смыслу ряда в разные виды
        незачем.
      */}
      <div
        className={`mb-4 flex-wrap items-center gap-2 rounded-lg p-3 md:flex ${filtersOpen ? 'flex' : 'hidden'}`}
        style={{ background: 'var(--otc-surface)', border: '1px solid var(--otc-line)' }}
      >
        {/*
          Поиск стоит ПЕРВЫМ и работает по всему году сразу.

          Номер счёта — самый частый способ его найти: он написан на бумаге,
          которую держат в руках. Клиника и юрлицо ищутся тем же полем, чтобы
          не заводить второе.
        */}
        {/*
          ПОИСК КРУПНЕЕ ОСТАЛЬНЫХ отборов — с ним начинают работу.

          Поле было в один кегль с плашками рядом (12 точек, высота 30) и
          терялось среди них, хотя счёт чаще всего ищут по номеру с бумаги
          в руках. Теперь оно и шире, и выше, и набирается в нём обычным
          размером — как в «Договорах» на соседней вкладке.

          Лупа внутри поля, отступ под неё строчным стилем: класс `pl-*`
          перебивается сокращённой записью `padding` из общего вида полей.
        */}
        <span className="relative inline-block w-full sm:w-auto">
          <Search
            size={16}
            className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2"
            style={{ color: 'var(--otc-ink2)' }}
          />
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={подсказкаПоиска('Поиск: № счёта, клиника…')}
            aria-label="Поиск счёта по номеру, клинике или юрлицу"
            /* Рамка ТОЛЩЕ и темнее, как у поиска в «Заказах»: с него
               начинают работу, и он не должен теряться среди плашек. */
            className="w-full rounded-md border-2 px-3 py-2 text-sm sm:w-auto"
            style={{
              borderColor: query ? 'var(--otc-accent)' : 'var(--otc-ink2)',
              background: 'var(--otc-surface)',
              color: 'var(--otc-ink)',
              minWidth: 260,
              paddingLeft: '2.25rem',
            }}
          />
        </span>
        {/* Юрлица — только те, что встречаются в выбранном месяце. */}
        <MultiSelectFilter
          label="Юрлицо"
          options={legalOptions}
          selected={legalIds}
          onChange={setLegalIds}
        />
        {/*
          БЕЗ ЦВЕТНЫХ ТОЧЕК в списках (решение владельца 28.09.2026).

          Здесь вариантов по два, и они — противоположности: «Оплачен» и
          «Не оплачен». Точка ничего не добавляет к словам, а цвет в меню
          спорил с плашками в строках, где тот же зелёный и красный значат
          состояние самого счёта, а не пункт меню.
        */}
        <MultiSelectFilter
          label="Статус оплаты"
          options={[
            { value: 'Оплачен', label: 'Оплачен' },
            { value: 'Не оплачен', label: 'Не оплачен' },
          ]}
          selected={paidFilter}
          onChange={setPaidFilter}
        />
        <MultiSelectFilter
          label="Статус отправки"
          options={[
            { value: 'Отправлен', label: 'Отправлен' },
            { value: 'Не отправлен', label: 'Не отправлен' },
          ]}
          selected={sentFilter}
          onChange={setSentFilter}
        />

        {/*
          ПОРЯДОК — кнопками, а не выпадающим списком.

          В списке из четырёх строк порядок приходилось раскрывать, читать
          и выбирать; здесь он виден целиком, и нажатие сразу переставляет
          журнал. Повторное нажатие по включённой кнопке возвращает порядок
          по дате — как снятие отбора.
        */}
        {/*
          «По сумме» — ТРИ состояния по кругу: выключено → от большей суммы
          к меньшей → от меньшей к большей → снова выключено. Так одной
          кнопкой закрываются оба вопроса — «где самые дорогие» и «где
          мелочь», — и не заводится вторая кнопка с обратным порядком.
        */}
        <ПорядокСчетов
          label="По сумме"
          направление={sort === 'sum' ? 'вниз' : sort === 'sumAsc' ? 'вверх' : null}
          onClick={() => setSort(sort === 'sum' ? 'sumAsc' : sort === 'sumAsc' ? 'new' : 'sum')}
          title={
            sort === 'sum'
              ? 'Сейчас: дорогие сверху. Нажмите — дешёвые сверху'
              : sort === 'sumAsc'
                ? 'Сейчас: дешёвые сверху. Нажмите — вернуть порядок по дате'
                : 'Разложить по сумме: дорогие сверху'
          }
        />
        {/*
          ЧЕРТА между отборами и действиями — как в «Заказах».

          Слева то, что СУЖАЕТ список; справа то, что меняет его вид:
          порядок и сброс. В сплошном ряду через одинаковый зазор они
          читались как ещё два отбора.
        */}
        <span
          className="flex flex-shrink-0 items-center gap-2 md:ml-1 md:border-l md:pl-3"
          style={{ borderColor: 'var(--otc-line)' }}
        >
          {/*
            «Сначала новые» — та же кнопка, что в журнале заказов: без рамки,
            подпись и птичка меняются вместе с порядком. Нажатие сбрасывает
            порядок по сумме: это один и тот же вопрос «как разложить»,
            а не два условия.

            Когда список разложен по сумме, кнопка показывает «Сначала новые»
            как предложение, а не как состояние: нажатие вернёт порядок
            по дате.
          */}
          <button
            onClick={() => setSort(sort === 'new' ? 'old' : 'new')}
            aria-pressed={sort === 'new'}
            className="inline-flex items-center gap-1.5 rounded-md px-2 py-1.5 text-xs font-medium text-ink hover:bg-black/5"
            title="Порядок счетов по дате"
          >
            {sort === 'old' ? (
              <>
                Сначала старые
                <ChevronUp size={14} className="flex-shrink-0" />
              </>
            ) : (
              <>
                Сначала новые
                <ChevronDown size={14} className="flex-shrink-0" />
              </>
            )}
          </button>

          {/*
            «Сбросить» — КАК В «ЗАКАЗАХ»: без рамки, приглушённым серым,
            с круговой стрелкой слева. В рамке она выглядела ещё одним
            отбором в ряду одинаковых плашек, и её нажимали, думая, что
            раскроют список.

            Снимает и выделение счетов заодно: отмечают обычно так —
            сузили список отбором, нажали «выбрать все». После сброса
            отобранного отмеченные пропадали с глаз, но оставались
            отмеченными, и внизу висела полоса с суммой, к которой на
            экране не было ни одной строки.
          */}
          <button
            onClick={() => {
              setLegalIds([]);
              setPaidFilter([]);
              setSentFilter([]);
              setQuery('');
              setSort('new');
              setSelected(new Set());
            }}
            className="inline-flex items-center gap-1.5 rounded-md px-2 py-1.5 text-xs text-ink2 hover:bg-black/5"
            title="Снять все отборы и вернуть обычный порядок"
          >
            <RotateCcw size={13} className="flex-shrink-0" />
            Сбросить
          </button>
        </span>

        {/*
          Масштаб — К КРАЮ ПАНЕЛИ, как в «Заказах».

          Там он прижат к дальнему краю ряда фильтров и всегда на одном
          месте, а здесь болтался сразу за «Сбросить» и ездил по строке
          вслед за длиной отборов: то у середины, то ближе к краю.
        */}
        <span className="md:ml-auto">
          <ZoomControl zoom={zoom} hint="Мельче — на экран влезет больше счетов" />
        </span>
      </div>

      {/*
        «Выбрать все» и ИТОГ — одной строкой над списком.

        Итог стоял в верхней строке, у правого края окна: на широком мониторе
        он оказывался в полуметре от самих счетов, и взгляд его не находил.
        Здесь он вплотную к списку, к которому и относится. Количество в нём
        больше не повторяется — оно уже написано слева, в «Выбрать все».
      */}
      <div className="mb-1.5 flex flex-wrap items-center gap-2 px-1">
        {visible.length > 0 && (
        <label className="flex items-center gap-2 text-[11px] text-ink2 md:text-xs">
          <input
            type="checkbox"
            checked={visible.every((invoice) => selected.has(invoice.id))}
            onChange={(event) =>
              setSelected((current) => {
                const next = new Set(current);
                for (const invoice of visible) {
                  if (event.target.checked) next.add(invoice.id);
                  else next.delete(invoice.id);
                }
                return next;
              })
            }
          />
          Выбрать все — {visible.length} {pluralRu(visible.length, ['счёт', 'счёта', 'счетов'])}
        </label>
        )}

        <span className="ml-auto text-[11px] text-ink2 md:text-xs">
          <span className="tabular font-medium text-ink">{formatKop(shownSumKop)} ₽</span>
          {unpaidSumKop > 0 && (
            <>
              {' · '}
              <span className="md:hidden">долг </span>
              <span className="hidden md:inline">не оплачено </span>
              <span className="tabular font-semibold" style={{ color: STATUS_COLORS.danger }}>
                {formatKop(unpaidSumKop)} ₽
              </span>
            </>
          )}
        </span>
      </div>

      {/* Масштаб — ТОЛЬКО на строках: строка «Выбрать все» с итогом остаётся
          прежней, как шапка таблицы в «Заказах». */}
      <div className="space-y-2" style={{ zoom: `${масштаб}%` }}>
        {visible.map((invoice) => {
          const clinic = bootstrap.data!.clinics.find((c) => c.id === invoice.clinicId);
          const legal = clinic?.legals.find((l) => l.id === invoice.legalId);
          const totalKop = invoice.items.reduce((sum, item) => sum + Math.round(item.priceKop * item.qty), 0);
          /*
            Две метки вместо одной.

            Сводная метка показывала что-то одно: у оплаченного счёта на
            месте отправки стояло «Оплачено», и понять, ушло ли письмо в
            клинику, по списку было нельзя. Это разные события — счёт
            бывает оплачен по бумажной копии и не отправлен вовсе.
          */
          /*
           * «Не отправлен» — ПРИГЛУШЁННОЙ СЕРОЙ, а не красной.
           *
           * Красная плашка означает беду, а неотправленный счёт — это просто
           * ещё не сделанный шаг, и таких в начале месяца половина списка.
           * Строка из двух красных плашек подряд читалась как «тут всё плохо»,
           * и на её фоне терялось действительно важное — неоплаченные счета
           * с деньгами.
           *
           * «Отправлен» остаётся сплошной тёмной: событие состоялось, и это
           * видно с одного взгляда.
           */
          const sentColor = invoice.sent ? 'var(--otc-accent)' : 'var(--otc-ink2)';
          /*
           * «Не оплачен» — КРАСНЫМ, а не серым.
           *
           * Серая метка читалась как «сведений нет» и терялась среди
           * оплаченных: в списке из полусотни счетов взгляд цеплялся за
           * зелёные, а неоплаченные — то, ради чего в список и заходят, —
           * оставались незаметными.
           *
           * Красный тот же, что у прочих неприятностей в программе, и по
           * насыщенности он равен зелёному «Оплачен»: белый текст читается
           * на обоих одинаково, и пара смотрится ровно. Бледнее сделать
           * нельзя — на светлой заливке белые буквы пропадут.
           *
           * Частичная оплата остаётся жёлтой: это не «не оплачен», а «оплачен
           * не весь», и смешивать их значило бы потерять единственное место,
           * где видно недоплату.
           */
          const paidColor = invoice.paid
            ? STATUS_COLORS.done
            : invoice.paidCount > 0
              ? 'var(--otc-amber, #AE5E1E)'
              : STATUS_COLORS.danger;

          return (
            /*
              Карточка счёта: на телефоне ТРИ строки, на широком экране — одна.

              На телефоне всё стояло сплошным потоком и переносилось как
              придётся: карточка вырастала в шесть строк, а на экран помещался
              один счёт. Теперь у каждой строки своё назначение —

                1. кто и когда: конверт, номер с клиникой, корзина справа;
                2. деньги: сумма, статус отправки и «Открыть» справа;
                3. документы: счёт и акт рядом, по два формата у каждого.

              На широком экране обёртки строк исчезают (`md:contents`), и все
              кнопки снова выстраиваются в один ряд — порядок там задан
              явными `md:order-*`, иначе корзина из первой строки встала бы
              в середину ряда.
            */
            /*
              ПОДСВЕТКА ПОД КУРСОРОМ и ДВОЙНОЙ ЩЕЛЧОК — как в строках журнала
              заказов (решение владельца 28.09.2026).

              Раньше счёт открывался только кнопкой с номером и кнопкой
              «Открыть», а сама строка на курсор не отзывалась: было неясно,
              что по ней вообще можно щёлкнуть. Теперь карточка ведёт себя
              как строка таблицы — подсвечивается и открывается двойным
              щелчком, а открытая видна и в списке.

              `select-none` обязателен: двойной щелчок по тексту иначе
              выделяет слово, и под открытой карточкой остаётся подсвеченный
              номер счёта.
            */
            <div
              key={invoice.id}
              onDoubleClick={() => открытьСчёт(invoice.id)}
              className="cursor-pointer select-none rounded-xl border px-3 py-2.5 transition-colors md:flex md:flex-wrap md:items-center md:gap-3 md:px-4 md:py-3"
              style={{
                borderColor: openId === invoice.id ? 'var(--otc-accent)' : 'var(--otc-line)',
                background:
                  openId === invoice.id
                    ? 'color-mix(in srgb, var(--otc-accent) 14%, var(--otc-surface))'
                    : 'var(--otc-surface)',
              }}
              /*
                Подсветка ЗАМЕТНАЯ: четырёх процентов серого на белом почти
                не видно, особенно на ноутбуке при дневном свете. Открытый
                счёт держит свою подсветку постоянно — по ней в длинном
                списке находят строку, к которой относится окно.
              */
              onMouseEnter={(e) => {
                if (openId !== invoice.id) {
                  e.currentTarget.style.background = 'color-mix(in srgb, var(--otc-ink) 9%, var(--otc-surface))';
                }
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background =
                  openId === invoice.id
                    ? 'color-mix(in srgb, var(--otc-accent) 14%, var(--otc-surface))'
                    : 'var(--otc-surface)';
              }}
            >
              {/* 1. Кто и когда */}
              <div className="flex items-start gap-2 md:contents">
                {/* Галочка — первым делом: с неё начинается любое действие над пачкой. */}
                <input
                  type="checkbox"
                  className="mt-1 flex-shrink-0 md:order-none"
                  checked={selected.has(invoice.id)}
                  onChange={() => toggle(invoice.id)}
                  aria-label={`Выбрать счёт №${invoiceNumberText(invoice.number, invoice.numberSuffix)}`}
                />
                <Mail
                  size={15}
                  className="mt-0.5 flex-shrink-0 md:order-1 md:mt-0"
                  style={{ color: invoice.sent ? STATUS_COLORS.done : STATUS_COLORS.danger }}
                />
                {/*
                  Номер счёта — НЕ КНОПКА.

                  Раньше он открывал карточку одним щелчком, а строка — двойным:
                  щёлкнув по номеру дважды, человек открывал счёт первым
                  щелчком и закрывал вторым, а по соседней строке попадал
                  в другой счёт. Теперь открытие ровно одно — двойной щелчок
                  по строке, как в журнале заказов; кнопка «Открыть» справа
                  осталась для тех, кто ищет её глазами.
                */}
                <div className="min-w-0 flex-1 text-left md:order-2">
                  <p className="text-sm text-ink">
                    <span className="tabular font-medium">№{invoiceNumberText(invoice.number, invoice.numberSuffix)}</span> · {clinic?.name} ·{' '}
                    {legal?.name}
                  </p>
                  <p className="text-xs text-ink2">
                    {formatDate(invoice.date)} · {invoice.items.length}{' '}
                    {pluralRu(invoice.items.length, ['позиция', 'позиции', 'позиций'])} ·{' '}
                    {invoice.orderIds.length}{' '}
                    {pluralRu(invoice.orderIds.length, ['заказ', 'заказа', 'заказов'])}
                  </p>
                </div>
                <button
                  onClick={() => setConfirmInvoice(invoice.id)}
                  className="flex-shrink-0 rounded-lg border px-2 py-1.5 text-sm text-ink2 transition-colors hover:border-[color:var(--otc-coral)] hover:text-[color:var(--otc-coral)] md:order-8 md:px-3 md:py-2"
                  style={{ borderColor: 'var(--otc-line)' }}
                  title="Удалить счёт и акт"
                >
                  <Trash2 size={14} />
                </button>
              </div>

              {/* 2. Деньги */}
              <div className="mt-1.5 flex items-center gap-2 md:contents">
                <span className="tabular text-sm text-ink md:order-3">{formatKop(totalKop)} ₽</span>
                <span className="flex flex-wrap items-center gap-1.5 md:order-4">
                  <Badge color={sentColor} muted={!invoice.sent}>
                    {invoice.sent ? 'Отправлен' : 'Не отправлен'}
                  </Badge>
                  <Badge color={paidColor}>
                    {invoice.paid
                      ? 'Оплачен'
                      : invoice.paidCount > 0
                        ? `Оплачен ${invoice.paidCount} из ${invoice.orderCount}`
                        : 'Не оплачен'}
                  </Badge>
                </span>
                <span className="ml-auto md:order-7 md:ml-0">
                  <Button variant="secondary" onClick={() => открытьСчёт(invoice.id)}>
                    <Eye size={14} /> Открыть
                  </Button>
                </span>
              </div>

              {/*
                3. Документы. Оба формата прямо в списке: раньше отсюда
                открывался только PDF, а Word приходилось искать, открыв счёт,
                — хотя чаще нужен он: в нём правят перед печатью.
              */}
              {/*
                Ровно ДВЕ колонки по половине ширины, а не поток с переносом.
                Потоком «Счёт» и «Акт» переставали помещаться рядом на
                телефонах с увеличенным системным шрифтом и расползались
                на две строки; сетка держит их рядом при любом кегле.
              */}
              <div className="mt-2 grid grid-cols-2 items-center gap-2 md:mt-0 md:contents">
                <span className="md:order-5">
                  <DocumentLinks invoiceId={invoice.id} kind="schet" label="Счёт" onError={(m: string) => show(m, 'error')} />
                </span>
                <span className="md:order-6">
                  <DocumentLinks invoiceId={invoice.id} kind="akt" label="Акт" onError={(m: string) => show(m, 'error')} />
                </span>
              </div>
            </div>
          );
        })}

        {visible.length === 0 && (
          <EmptyState
            text={
              all.length === 0
                ? 'Пока нет сформированных счетов и актов. Они создаются в разделе «Заказы»: выделите заказы и нажмите «Счёт/акт».'
                : 'Нет счетов и актов по заданным условиям'
            }
          />
        )}
      </div>

      {/*
        Панель действий над отмеченными — как в заказах.

        Появляется от первого же отмеченного счёта: одиночные действия так же
        нужны, как и пачкой, и заводить для них отдельный путь значило бы
        объяснять человеку два способа сделать одно.

        Документы отдаются АРХИВОМ даже для одного счёта: их всё равно два —
        счёт и акт, — и две загрузки подряд браузер уже встречает подозрением.
      */}
      {chosen.length > 0 && (
        <div className="pointer-events-none fixed inset-x-0 bottom-0 z-40 px-2 pb-2 md:px-4 md:pb-4">
          {/*
            Панель по ширине СОДЕРЖИМОГО, а не во весь экран, и в одну строку.

            Растянутая на всю ширину и разбитая надвое, она выглядела как ещё
            один раздел страницы: три кнопки терялись в пустом поле, и было
            неясно, к чему они относятся. Действий немного — им хватает строки,
            а плотный островок над списком читается как то, что появилось
            в ответ на выделение.
          */}
          {/*
            Высота и вид — КАК В «ЗАКАЗАХ» (решение владельца 28.09.2026).

            Там панель выделения выше и кнопки в ней стоят значком над
            подписью; здесь они были в одну строку и мельче, и две панели
            в соседних разделах читались как сделанные разными руками.
            Теперь обе собраны из одного кусочка — `SelectionActionButton`.
          */}
          <div
            className="pointer-events-auto mx-auto flex w-fit max-w-full flex-wrap items-center gap-x-1 gap-y-1 rounded-2xl border px-2 py-2 shadow-lg"
            style={{ borderColor: 'var(--otc-line)', background: 'var(--otc-surface)' }}
          >
            {/*
              ТЕЛЕФОН: счётчик и крестик — отдельной строкой сверху, кнопки
              под ними. В одну строку они не помещались: счётчик с суммой
              плюс четыре подписи шире любого телефона, и «Снять» сползало
              вниз отдельной строкой, читаясь как случайная ссылка.
            */}
            <div className="flex w-full items-center gap-2 px-1.5 pb-1 md:hidden">
              {счётчикВыбранного}
              <span className="ml-auto">{кнопкаСнять}</span>
            </div>

            <span className="hidden px-2 md:flex">{счётчикВыбранного}</span>

            <div className="my-1 hidden w-px self-stretch md:block" style={{ background: 'var(--otc-line)' }} />

            {/*
              Подписи полные: «Скачать архив Word» говорит и что произойдёт,
              и в каком виде придёт. На телефоне остаётся «Word» — там строка
              в дефиците, а значок загрузки рядом объясняет остальное.
            */}
            <BarAction
              icon={Download}
              full={archiving === 'docx' ? 'Собираю архив…' : 'Скачать архив Word'}
              short={archiving === 'docx' ? '…' : 'Word'}
              color="var(--otc-accent)"
              disabled={archiving !== null || busy}
              onClick={() => void grabArchive('docx')}
            />
            <BarAction
              icon={FileText}
              full={archiving === 'pdf' ? 'Собираю архив…' : 'Скачать архив PDF'}
              short={archiving === 'pdf' ? '…' : 'PDF'}
              color="var(--otc-accent)"
              disabled={archiving !== null || busy}
              onClick={() => void grabArchive('pdf')}
            />
            {/*
              ОТМЕТИТЬ ОПЛАЧЕННЫМИ — как в полосе выделения «Заказов».

              Стоит ПЕРЕД удалением и отделено от него: это обычное дело
              месяца, а удаление — исключение, и соседство одинаковых кнопок
              приводило бы к промахам.
            */}
            <BarAction
              icon={CreditCard}
              full="Отметить выбранные счета оплаченными"
              short="Оплачено"
              color={STATUS_COLORS.done}
              disabled={archiving !== null || busy}
              onClick={() => setConfirmPaid(true)}
            />

            <div className="my-1 hidden w-px self-stretch md:block" style={{ background: 'var(--otc-line)' }} />

            <BarAction
              icon={Trash2}
              full="Удалить"
              short="Удалить"
              color="var(--otc-coral)"
              disabled={archiving !== null || busy}
              onClick={() => setConfirmBulk(true)}
            />

            {/* На широком экране крестик стоит в конце ряда, как в «Заказах». */}
            <span className="hidden md:block">{кнопкаСнять}</span>
          </div>
        </div>
      )}

      {/*
        Подтверждение спрашивается и здесь, как при отметке одного счёта:
        оплата тянет за собой заказы, и снимать её потом пришлось бы по
        одному. Сколько именно счетов ждёт оплаты — написано в заголовке,
        чтобы «выбрал 10, оплатились 3» не стало неожиданностью.
      */}
      {confirmPaid && (
        <ConfirmDialog
          title={
            ждутОплаты.length === 0
              ? 'Все выбранные счета уже оплачены'
              : `Отметить оплаченными ${ждутОплаты.length} ${pluralRu(ждутОплаты.length, ['счёт', 'счёта', 'счетов'])}?`
          }
          description="Вместе со счетами оплаченными станут и все заказы, которые в них входят."
          details={
            ждутОплаты.length > 0 ? (
              <span>
                Номера:{' '}
                <span className="tabular">
                  {ждутОплаты.map((i) => `№${invoiceNumberText(i.number, i.numberSuffix)}`).join(', ')}
                </span>
                {chosen.length > ждутОплаты.length && (
                  <span className="text-ink2">
                    {' '}
                    · уже оплачено и пропущено: {chosen.length - ждутОплаты.length}
                  </span>
                )}
              </span>
            ) : undefined
          }
          confirmLabel={busy ? 'Отмечаю…' : 'Да, оплачены'}
          onConfirm={() => void отметитьОплаченными()}
          onCancel={() => setConfirmPaid(false)}
        />
      )}

      {confirmBulk && (
        <ConfirmDialog
          title={`Удалить ${chosen.length} ${pluralRu(chosen.length, ['счёт', 'счёта', 'счетов'])}?`}
          description="Заказы, которые в них входят, вернутся в список свободных — их можно будет включить в другой счёт."
          details={
            <span>
              Номера:{' '}
              <span className="tabular">
                {chosen.map((i) => `№${invoiceNumberText(i.number, i.numberSuffix)}`).join(', ')}
              </span>
            </span>
          }
          confirmLabel={busy ? 'Удаление…' : 'Да, удалить'}
          onConfirm={() => void removeChosen()}
          onCancel={() => setConfirmBulk(false)}
        />
      )}

      {/* Удаление прямо из списка — открывать счёт ради этого незачем. */}
      {invoiceToDelete && (
        <ConfirmDialog
          title={`Удалить счёт и акт №${invoiceNumberText(invoiceToDelete.number, invoiceToDelete.numberSuffix)}?`}
          description="Документы исчезнут из истории, а заказы вернутся в свободные — их можно будет включить в другой счёт."
          details={
            <p>
              {formatDate(invoiceToDelete.date)} · позиций: {invoiceToDelete.items.length} · заказов:{' '}
              {invoiceToDelete.orderIds.length}
            </p>
          }
          confirmLabel={busy ? 'Удаление…' : 'Удалить счёт и акт'}
          onConfirm={() => void removeInvoice(invoiceToDelete)}
          onCancel={() => setConfirmInvoice(null)}
        />
      )}

      {openInvoice && (
        <InvoiceDetailModal
          invoice={openInvoice}
          bootstrap={bootstrap.data}
          onClose={() => setOpenId(null)}
          onChanged={(message, держатьОткрытым) => {
            if (message) show(message);
            void invoices.refetch();
            if (!держатьОткрытым) setOpenId(null);
          }}
          onDeleted={(message) => {
            show(message);
            void invoices.refetch();
            setOpenId(null);
          }}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------

/**
 * Договоры — сводка по юрлицам всех клиник.
 *
 * Отдельная вкладка нужна затем, чтобы увидеть разом, у кого договор
 * не заведён: строка договора печатается в счёте как «Основание», и пустое
 * значение обнаруживается обычно в момент отправки документа.
 */
function ContractsTab({
  show,
  масштаб = 100,
}: {
  show: (text: string, tone?: 'info' | 'error') => void;
  /** Размер СТРОК списка. Отборы и заголовки от него не зависят. */
  масштаб?: number;
}) {
  const bootstrap = useBootstrap(true);
  const [contractParams] = useSearchParams();
  const [openedLegal, setOpenedLegal] = useState<string | null>(() => contractParams.get('createContract'));
  const [discardEdit, setDiscardEdit] = useState(false);
  const [contractCreating, setContractCreating] = useState(false);
  const [confirmLegal, setConfirmLegal] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  /** Поиск по клинике, юрлицу и номеру договора. */
  const [search, setSearch] = useState('');
  /** Показать юрлица без номера договора для счетов. */
  const [onlyMissing, setOnlyMissing] = useState(false);
  /** Файл, который сейчас загружается: показываем, у какой строки крутится. */
  const [uploading, setUploading] = useState<string | null>(null);
  /** Файл, который предложено удалить. */
  const [confirmFile, setConfirmFile] = useState<{ legalId: string; fileId: string; name: string } | null>(null);

  /*
   * Кому кладём файл — в ref, а не в состоянии.
   *
   * Между нажатием на скрепку и выбором файла проходит окно операционной
   * системы: перерисовки в это время не происходит, а состояние вызвало бы
   * лишнюю. Читается значение один раз, в обработчике выбора.
   */
  const fileInputRef = useRef<HTMLInputElement>(null);
  const uploadTargetRef = useRef<string | null>(null);

  const выбратьФайл = (legalId: string) => {
    uploadTargetRef.current = legalId;
    fileInputRef.current?.click();
  };

  /**
   * Правка договора — ПРЯМО ЗДЕСЬ, а не переходом в «Клиники».
   *
   * Кнопка «Изменить» уводила в другой раздел, и человек оказывался не там,
   * куда шёл: он открыл договоры, чтобы поправить договор. Всё нужное для
   * этого — номер, дата и почта для счетов — правится в строке; остальные
   * реквизиты юрлица по-прежнему живут в карточке клиники.
   */
  const [editRow, setEditRow] = useState<{
    id: string;
    version: number;
    contractNo: string;
    contractDate: string;
    email: string;
  } | null>(null);

  const начатьПравку = (legal: { id: string; version: number; contractNo?: string | null; contractDate?: string | null; email?: string | null }) => ({
    id: legal.id,
    version: legal.version,
    contractNo: legal.contractNo ?? '',
    contractDate: legal.contractDate ?? '',
    email: legal.email ?? '',
  });

  const saveRow = async () => {
    if (!editRow) return;
    setBusy(true);
    try {
      await api.post('/api/modules/clinics/commit', {
        creates: [],
        updates: [
          {
            id: editRow.id,
            kind: 'legal',
            version: editRow.version,
            patch: {
              contractNo: editRow.contractNo.trim(),
              contractDate: editRow.contractDate,
              email: editRow.email.trim(),
            },
          },
        ],
        deletes: [],
      });
      setEditRow(null);
      void bootstrap.refetch();
    } catch (err) {
      show(err instanceof Error ? err.message : 'Не удалось сохранить', 'error');
    } finally {
      setBusy(false);
    }
  };

  const все = (bootstrap.data?.clinics ?? []).flatMap((clinic) =>
    clinic.legals.map((legal) => ({ clinicId: clinic.id, clinic: clinic.name, legal })),
  );

  /*
   * Отбор: поиск и «только без договора».
   *
   * Юрлиц под сорок, и глазами среди них искать нечего. Поиск идёт по клинике,
   * юрлицу и НОМЕРУ договора: номер написан на бумаге, которую держат в руках,
   * — искать по нему так же естественно, как по названию.
   */
  const запрос = search.trim().toLowerCase();
  const rows = все.filter((r) => {
    if (onlyMissing && !(needsContract(r.legal.pay) && !r.legal.contractNo)) return false;
    if (!запрос) return true;
    return (
      r.clinic.toLowerCase().includes(запрос) ||
      r.legal.name.toLowerCase().includes(запрос) ||
      (r.legal.contractNo ?? '').toLowerCase().includes(запрос.replace(/^№\s*/, ''))
    );
  });
  /*
   * Считаются только те, кому договор ДЕЙСТВИТЕЛЬНО нужен. Клиника, платящая
   * наличными, счетов не получает: требовать от неё договор — требовать
   * заполнить поля, которые никуда не попадут.
   */
  const missing = все.filter((r) => needsContract(r.legal.pay) && !r.legal.contractNo).length;
  const opened = все.find(r => r.legal.id === openedLegal);
  const closeCard = () => {
    if (busy || uploading || contractCreating || confirmLegal || confirmFile || discardEdit) return;
    if (editRow && opened && JSON.stringify(editRow) !== JSON.stringify(начатьПравку(opened.legal))) { setDiscardEdit(true); return; }
    setEditRow(null); setOpenedLegal(null);
  };
  const toClear = все.find((r) => r.legal.id === confirmLegal) ?? null;

  /**
   * Договор ОЧИЩАЕТСЯ, а не удаляется.
   *
   * Строка этой вкладки — не отдельный документ, а юрлицо клиники. Удалить её
   * значило бы стереть юрлицо со всеми реквизитами и филиалами, а нужно всего
   * лишь убрать номер и дату договора: клиника осталась, договор с ней —
   * закончился. Само юрлицо удаляется в разделе «Клиники», где видно, что
   * ещё на нём висит.
   */
  /**
   * Загрузка файла договора.
   *
   * Файл уходит на сервер сразу, без «Сохранить»: это не правка черновика,
   * а появление документа. Кнопка сохранения модуля к нему отношения не имеет
   * и ждать её было бы странно — человек приложил договор и ждёт, что он там.
   */
  const uploadFile = async (legalId: string, файл: File) => {
    setUploading(legalId);
    try {
      const form = new FormData();
      form.append('file', файл);
      const ответ = await fetch(`/api/legals/${legalId}/files`, {
        method: 'POST',
        body: form,
        credentials: 'same-origin',
      });
      if (!ответ.ok) {
        const тело = await ответ.json().catch(() => ({}));
        throw new Error((тело as { error?: string }).error ?? 'Не удалось загрузить файл');
      }
      show(`Файл «${файл.name}» приложен`);
      void bootstrap.refetch();
    } catch (error) {
      show(error instanceof Error ? error.message : 'Не удалось загрузить файл', 'error');
    } finally {
      setUploading(null);
    }
  };

  const deleteFile = async (legalId: string, fileId: string, name: string) => {
    setBusy(true);
    try {
      const ответ = await fetch(`/api/legals/${legalId}/files/${fileId}`, {
        method: 'DELETE',
        credentials: 'same-origin',
      });
      if (!ответ.ok) throw new Error('Не удалось удалить файл');
      show(`Файл «${name}» удалён`);
      setConfirmFile(null);
      void bootstrap.refetch();
    } catch (error) {
      show(error instanceof Error ? error.message : 'Не удалось удалить файл', 'error');
    } finally {
      setBusy(false);
    }
  };

  const clearContract = async (legalId: string, version: number, label: string) => {
    setBusy(true);
    try {
      await api.post('/api/modules/clinics/commit', {
        creates: [],
        updates: [
          { id: legalId, kind: 'legal', version, patch: { contractNo: '', contractDate: '', contract: '' } },
        ],
        deletes: [],
      });
      show(`Договор ${label} очищен`);
      setConfirmLegal(null);
      void bootstrap.refetch();
    } catch (error) {
      show(error instanceof Error ? error.message : 'Не удалось очистить договор', 'error');
    } finally {
      setBusy(false);
    }
  };

  if (bootstrap.isLoading || !bootstrap.data) return <Spinner />;

  return (
    <div>
      {/*
        Полоса отбора: поиск и счётчик-переключатель.

        Счётчик незаведённых договоров сам является кнопкой отбора. Увидев
        «без договора: 3», по нему сразу и нажимают — иначе эти три плитки
        пришлось бы искать глазами среди сорока.
      */}
      <div className="contracts-toolbar mb-4 flex flex-wrap items-center gap-3 rounded-2xl border p-3" style={{borderColor:"var(--otc-line)",background:"var(--otc-surface)"}}>
        <div className="relative min-w-0 basis-full sm:basis-auto sm:flex-1">
          <Search
            size={14}
            className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2"
            style={{ color: 'var(--otc-ink2)' }}
          />
          <input
            className="otc-input w-full py-1.5 text-sm"
            /*
              Отступ под лупу — СТИЛЕМ, а не классом.

              Общий вид полей задаёт `padding` сокращённой записью, и она
              перебивала `pl-9`: подпись «Поиск…» начиналась под значком,
              буквы налезали на лупу. Свойство в самом элементе перебить
              нечем, поэтому отступ стоит здесь.
            */
            style={{ paddingLeft: '2.25rem', paddingRight: '0.75rem' }}
            placeholder={подсказкаПоиска('Клиника, юрлицо или номер договора')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        <button
          onClick={() => setOnlyMissing((v) => !v)}
          disabled={missing === 0}
          className="whitespace-nowrap rounded-full border px-3 py-1.5 text-xs font-medium transition-colors disabled:opacity-60"
          style={{
            borderColor: onlyMissing ? 'var(--otc-coral)' : 'var(--otc-line)',
            background: onlyMissing ? 'var(--otc-coral)' : 'transparent',
            color: onlyMissing ? '#fff' : missing > 0 ? 'var(--otc-coral)' : 'var(--otc-ink2)',
          }}
          title="Показать только тех, кому договор нужен, а его нет"
        >
          {missing > 0 ? `Без номера договора: ${missing}` : 'Договоры заведены у всех'}
        </button>

        <span className="text-xs text-ink2">
          {rows.length} {pluralRu(rows.length, ['юрлицо', 'юрлица', 'юрлиц'])} · файлов приложено{' '}
          {все.reduce((n, r) => n + r.legal.files.length, 0)}
        </span>
      </div>

      {rows.length === 0 ? (
        <EmptyState
          text={onlyMissing ? 'Договоры заведены у всех, кому они нужны.' : 'Ничего не нашлось.'}
        />
      ) : (
        /* Масштаб — только на плитках: поиск и переключатель над ними
           остаются прежними, как органы управления в «Заказах». */
        <div style={{ zoom: `${масштаб}%` }}>
        <CardGrid compact>
          {rows.map(({clinic,legal}) => <Card key={legal.id} title={clinic} subtitle={legalTitleText(legal)} onOpen={()=>setOpenedLegal(legal.id)} onDelete={null} deleteTitle="">
            <p className="mt-2 flex items-center gap-2 text-sm" style={{color:!legal.contractNo && !legal.files.length && needsContract(legal.pay)?'var(--otc-coral)':'var(--otc-ink)'}}><FileSignature size={15}/><span>{contractCardStatus(legal)}</span></p>
            <p className="mt-2 truncate text-xs text-ink2">{legal.email || (needsEmail(legal.pay)?'Почта для счетов не указана':'Почта не требуется')}</p>
            <span className="mt-auto flex items-center justify-between gap-2 pt-5 text-xs text-ink2"><span className="flex items-center gap-1.5"><Paperclip size={14}/>{legal.files.length ? legal.files.length+' '+pluralRu(legal.files.length,['файл','файла','файлов']):'Без вложений'}</span><span>Открыть</span></span>
          </Card>)}
        </CardGrid>
        </div>
      )}

      {/*
        Один скрытый выбор файла на всю вкладку, а не по одному в каждой плитке:
        сорок невидимых полей ввода — сорок лишних узлов ради того же самого.
      */}
      <input
        ref={fileInputRef}
        type="file"
        accept=".pdf,.docx,.doc,.jpg,.jpeg,.png"
        className="hidden"
        onChange={(e) => {
          const файл = e.target.files?.[0];
          const кому = uploadTargetRef.current;
          // Сброс значения обязателен: иначе тот же файл второй раз не выберется.
          e.target.value = '';
          if (файл && кому) void uploadFile(кому, файл);
        }}
      />

      {opened && (()=>{const {legal,clinic}=opened;const правится=editRow?.id===legal.id;const нужен=needsContract(legal.pay);return <Modal title={clinic} size="lg" onClose={closeCard} footer={<div className="flex flex-wrap gap-2"><Button variant="secondary" onClick={()=>setEditRow(правится?null:начатьПравку(legal))} disabled={busy}>{правится?'Отмена':'Редактировать'}</Button><Button variant="secondary" onClick={()=>выбратьФайл(legal.id)} disabled={uploading!==null}><Paperclip size={15}/>Приложить</Button>{(legal.contractNo||legal.contractDate)&&<Button variant="secondary" onClick={()=>setConfirmLegal(legal.id)} disabled={busy}>Очистить номер и дату</Button>}<Button variant="secondary" onClick={closeCard}>Закрыть</Button></div>}><p className="mb-4 text-sm text-ink2">{legalTitleText(legal)}</p>
                {правится ? (
                  /*
                    Правка ПРЯМО В ПЛИТКЕ, а не переходом в «Клиники».

                    Человек открыл договоры, чтобы поправить договор; уводить
                    его за этим в другой раздел — значит потерять место, где он
                    работал. Здесь правится только то, что относится к договору:
                    номер, дата и почта для счетов. Банк, ИНН и подписант живут
                    в карточке клиники, и делать их копию здесь незачем.
                  */
                  <div className="flex flex-col gap-2">
                    <div className="flex gap-2">
                      <label className="flex-1">
                        <span className="mb-1 block text-[10px] uppercase tracking-wide text-ink2">Номер</span>
                        <input
                          className="otc-input tabular w-full px-2 py-1 text-xs"
                          placeholder="Номер договора"
                          value={editRow.contractNo}
                          onChange={(e) => setEditRow({ ...editRow, contractNo: e.target.value })}
                        />
                      </label>
                      <label className="flex-1">
                        <span className="mb-1 block text-[10px] uppercase tracking-wide text-ink2">Дата</span>
                        <input
                          type="date"
                          className="otc-input w-full px-2 py-1 text-xs"
                          value={editRow.contractDate}
                          onChange={(e) => setEditRow({ ...editRow, contractDate: e.target.value })}
                        />
                      </label>
                    </div>
                    <label>
                      <span className="mb-1 block text-[10px] uppercase tracking-wide text-ink2">
                        Почта для счетов
                      </span>
                      <input
                        className="otc-input w-full px-2 py-1 text-xs"
                        placeholder="buh@clinic.ru"
                        value={editRow.email}
                        onChange={(e) => setEditRow({ ...editRow, email: e.target.value })}
                      />
                    </label>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => void saveRow()}
                        disabled={busy}
                        className="inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-xs font-medium text-white disabled:opacity-50"
                        style={{ background: 'var(--otc-accent)' }}
                      >
                        <Check size={13} /> {busy ? 'Сохранение…' : 'Сохранить'}
                      </button>
                      <button
                        onClick={() => setEditRow(null)}
                        className="otc-btn otc-btn-secondary otc-btn-sm"
                        style={{ borderColor: 'var(--otc-line)' }}
                      >
                        Отмена
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    {/*
                      Цвет по общему правилу плиток: красным помечается только
                      то, из-за чего запись нерабочая. Отсутствие договора у
                      клиники по договору — как раз такой случай: в счёте
                      «Основание» останется пустым. У наличной клиники это
                      обычное состояние, и оно набрано серым.
                    */}
                    <p
                      className="mb-3 mt-3 flex items-start gap-2 text-sm font-medium leading-relaxed"
                      style={{ color: !legal.contractNo && !legal.files.length && нужен ? 'var(--otc-coral)' : 'var(--otc-ink2)' }}
                    >
                      <FileSignature size={12} className="mt-0.5 flex-shrink-0 opacity-60" />
                      <span className="min-w-0">
                        {contractCardStatus(legal)}
                      </span>
                    </p>

                    {!legal.contractNo && legal.files.length > 0 && <p className="mb-3 text-xs text-ink2">Файлы сохранены. Номер и дата для счетов ещё не указаны — их можно заполнить через «Редактировать».</p>}
                    <CardFact icon={CreditCard} value={legal.pay} empty="способ оплаты не задан" />

                    <CardFact
                      icon={Mail}
                      value={legal.email ?? ''}
                      empty={needsEmail(legal.pay) ? 'почта для счетов не указана' : 'почта не требуется'}
                      breakAll
                    />

                    {/*
                      Файлы договора.

                      Скачивание — обычной ссылкой: браузер сам предложит
                      сохранить, а имя возьмёт из заголовка ответа. Крестик, а
                      не корзина: корзина в этой вкладке означает ровно одно —
                      «очистить договор», и вторая корзина рядом с другим
                      смыслом сбивала бы с толку.
                    */}
                    {legal.files.length > 0 && (
                      <div className="mt-1.5 flex flex-wrap gap-1">
                        {legal.files.map((файл) => (
                          <span
                            key={файл.id}
                            className="inline-flex w-full min-w-0 items-center gap-2 rounded-lg border px-3 py-2 text-xs"
                            style={{ borderColor: 'var(--otc-line)', background: 'var(--otc-bg)' }}
                          >
                            <Paperclip size={10} className="flex-shrink-0 opacity-60" />
                            <a
                              href={`/api/legals/${legal.id}/files/${файл.id}`}
                              className="min-w-0 flex-1 break-words text-ink hover:underline"
                              title={`Скачать · ${размерФайла(файл.size)}`}
                            >
                              {файл.originalName}
                            </a>
                            <button
                              onClick={() =>
                                setConfirmFile({
                                  legalId: legal.id,
                                  fileId: файл.id,
                                  name: файл.originalName,
                                })
                              }
                              className="flex-shrink-0 text-ink2 transition-colors hover:text-[color:var(--otc-coral)]"
                              title="Удалить файл"
                              aria-label={`Удалить файл ${файл.originalName}`}
                            >
                              <X size={11} />
                            </button>
                          </span>
                        ))}
                      </div>
                    )}

                    <div className="mt-3"><ContractCreateButton legalId={legal.id} onDialogChange={setContractCreating}/></div>
                    {uploading === legal.id && (
                      <p className="mt-1.5 text-[11px] text-ink2">Файл загружается…</p>
                    )}
                  </>
                )}
      </Modal>})()}
      {discardEdit && <ConfirmDialog title="Закрыть без сохранения?" description="Изменения номера, даты и почты ещё не сохранены." confirmLabel="Закрыть без сохранения" onConfirm={()=>{setDiscardEdit(false);setEditRow(null);setOpenedLegal(null);}} onCancel={()=>setDiscardEdit(false)}/>}

      {toClear && (
        <ConfirmDialog
          title={`Очистить договор ${legalTitleText(toClear.legal)}?`}
          description="Номер и дата договора будут стёрты. Юрлицо, реквизиты, филиалы, почта и приложенные файлы останутся на месте."
          details={
            <p>
              Сейчас: {toClear.legal.contract || `№ ${toClear.legal.contractNo}`} · клиника{' '}
              {toClear.clinic}
              <span className="mt-1 block text-xs text-ink2">
                В новых счетах «Основание» останется пустым, пока договор не заведут заново.
                Уже выставленные счета не изменятся — они хранят свой снимок.
              </span>
            </p>
          }
          confirmLabel={busy ? 'Очистка…' : 'Очистить договор'}
          onConfirm={() =>
            void clearContract(
              toClear.legal.id,
              toClear.legal.version,
              legalTitleText(toClear.legal),
            )
          }
          onCancel={() => setConfirmLegal(null)}
        />
      )}

      {confirmFile && (
        <ConfirmDialog
          title="Удалить файл договора?"
          description="Файл будет стёрт с сервера. Номер и дата договора останутся на месте."
          details={<p>{confirmFile.name}</p>}
          confirmLabel={busy ? 'Удаление…' : 'Удалить файл'}
          onConfirm={() => void deleteFile(confirmFile.legalId, confirmFile.fileId, confirmFile.name)}
          onCancel={() => setConfirmFile(null)}
        />
      )}
    </div>
  );
}

/** Размер файла человеческими единицами — для подсказки над именем. */
function размерФайла(байт: number): string {
  if (байт < 1024) return `${байт} Б`;
  if (байт < 1024 * 1024) return `${Math.round(байт / 1024)} КБ`;
  return `${(байт / 1024 / 1024).toFixed(1).replace('.', ',')} МБ`;
}

// ---------------------------------------------------------------------------

/**
 * Пример, на котором показывается подстановка.
 *
 * Фамилия ЖЕНСКАЯ намеренно: на «Ивановой М.» правило родительного падежа
 * видно, а на «Петрова» его можно принять за случайность.
 */
const LETTER_SAMPLE = {
  doctor: 'Карепов И.А.',
  patient: 'Иванова М.',
  /*
    В образце конструкция С ДОП. ЭЛЕМЕНТАМИ — так письмо и уходит.
    Раньше здесь стояло одно название, и по предпросмотру нельзя было понять,
    попадут ли в письмо печать модели и крючки.
  */
  construction: 'Ретейнер Hawley + Печать 3D модели',
  due: '14.04.2026',
};

/** Список подстановок — из общего набора, чтобы подсказка не расходилась с делом. */
function Placeholders() {
  return (
    /*
      Подстановки — ПИЛЮЛЯМИ, а на телефоне ещё и без пояснений.

      Пояснения к пяти меткам занимали на телефоне пять строк мелкого текста
      подряд, и над каждым полем стояла серая простыня: само поле и
      предпросмотр терялись в ней. Метка говорит за себя, а пояснение
      остаётся во всплывающей подписи и на широком экране.
    */
    <div className="mb-2.5 flex flex-wrap gap-1.5 text-[11px] text-ink2">
      {LETTER_PLACEHOLDERS.map((item) => (
        <span
          key={item.token}
          title={item.hint}
          className="rounded-full border px-2 py-0.5"
          style={{ borderColor: 'var(--otc-line)' }}
        >
          <code className="text-ink">{item.token}</code>
          <span className="hidden md:inline"> — {item.hint}</span>
        </span>
      ))}
    </div>
  );
}

/**
 * Предпросмотр письма.
 *
 * Нужен именно здесь: падеж пациента подставляется правилом, и без примера
 * непонятно, что «{пациент}» превратится в «Ивановой М.». Заодно сразу видно
 * опечатку в метке — она останется в тексте фигурными скобками.
 */
function LetterPreview({ template }: { template: string }) {
  if (!template.trim()) return null;
  return (
    <p
      className="mt-2.5 rounded-lg border px-3 py-2 text-xs leading-relaxed text-ink"
      style={{ borderColor: 'var(--otc-line)', background: 'var(--otc-bg)' }}
    >
      <span className="mr-1.5 text-ink2">Врач получит:</span>
      {fillLetter(template, LETTER_SAMPLE)}
    </p>
  );
}


/**
 * Шаблоны писем: уведомления врачу и сопроводительное к счёту.
 *
 * У писем врачу есть подстановки — пациент, конструкция, срок. У письма со
 * счётом их нет намеренно: номер и дата стоят в теме и во вложениях, и третье
 * место, где они могут разойтись, ни к чему.
 */
function TemplatesTab({ show }: { show: (text: string, tone?: 'info' | 'error') => void }) {
  const bootstrap = useBootstrap(true);
  const module = useModule('templates');

  const templates = bootstrap.data?.settings?.templates;
  const [draft, setDraft] = useState<{
    letter1: string;
    letter2: string;
    invoiceTemplate: string;
  } | null>(null);

  if (bootstrap.isLoading || !bootstrap.data?.settings) return <Spinner />;

  const current = draft ?? {
    letter1: templates?.letter1 ?? '',
    letter2: templates?.letter2 ?? '',
    /*
     * Текст по умолчанию подставляется В ПОЛЕ, а не бледной подсказкой.
     * Подсказка выглядит как заполненное поле, но им не является: ровно на
     * этом уже обожглись с реквизитами почты — сохранился один пароль, а
     * сервер и логин остались пустыми, потому что серый пример приняли за
     * значение.
     */
    invoiceTemplate: templates?.invoiceTemplate?.trim() || DEFAULT_INVOICE_LETTER,
  };
  const changed = draft !== null;

  const save = async () => {
    const settings = bootstrap.data!.settings!;
    useDrafts.getState().load('templates', 'settings', [{ id: '1', version: settings.version }]);
    useDrafts.getState().update('templates', 'settings', '1', {
      templates: { ...templates, ...current },
    });

    const outcome = await module.save();
    if (outcome.ok) {
      setDraft(null);
      void bootstrap.refetch();
    } else {
      show(outcome.error ?? 'Не удалось сохранить', 'error');
    }
  };

  return (
    <div className="max-w-3xl space-y-5">
      {/*
        «Сохранить» и «Отменить» — СВЕРХУ, как в остальных разделах.

        Внизу их искали не там: во всех прочих экранах эта пара стоит в шапке,
        и привычка ведёт взгляд туда. Здесь шапка общая на четыре вкладки,
        а черновик есть только у шаблонов, поэтому пара живёт в самой вкладке —
        но в её начале.
      */}
      <div className="flex flex-wrap items-center gap-2">
        <SaveDiscard
          dirty={changed}
          saving={module.saving}
          onSave={() => void save()}
          onDiscard={() => {
            module.discard();
            show('Изменения отменены');
          }}
          what="Шаблоны"
        />
      </div>

      <div className="rounded-xl border p-4 md:p-5" style={{ borderColor: 'var(--otc-line)', background: 'var(--otc-surface)' }}>
        <p className="mb-1 text-sm font-medium text-ink">Уведомление о приёме заказа</p>
        <Placeholders />
        <textarea
          className="otc-input resize-none"
          rows={4}
          value={current.letter1}
          onChange={(e) => setDraft({ ...current, letter1: e.target.value })}
        />
        <LetterPreview template={current.letter1} />
      </div>

      <div className="rounded-xl border p-4 md:p-5" style={{ borderColor: 'var(--otc-line)', background: 'var(--otc-surface)' }}>
        <p className="mb-1 text-sm font-medium text-ink">Уведомление о готовности</p>
        <p className="mb-2 text-xs text-ink2">Те же подстановки</p>
        <textarea
          className="otc-input resize-none"
          rows={4}
          value={current.letter2}
          onChange={(e) => setDraft({ ...current, letter2: e.target.value })}
        />
        <LetterPreview template={current.letter2} />
      </div>

      {/*
        Письмо со счётом — без подстановок: номер и дата стоят в теме письма и
        во вложениях. Пустое поле означает текст по умолчанию, а не пустое
        письмо: бухгалтерия получила бы вложения без единого слова.
      */}
      <div className="rounded-xl border p-4 md:p-5" style={{ borderColor: 'var(--otc-line)', background: 'var(--otc-surface)' }}>
        <p className="mb-1 text-sm font-medium text-ink">Письмо со счётом и актом</p>
        <p className="mb-2 text-xs text-ink2">
          Уходит клинике вместе с двумя вложениями в Word. Номер и дата подставляются в тему письма.
        </p>
        <textarea
          className="otc-input resize-none"
          rows={6}
          value={current.invoiceTemplate}
          onChange={(e) => setDraft({ ...current, invoiceTemplate: e.target.value })}
        />
        {/*
          Подсказка — ТОЙ ЖЕ плашкой, что «Врач получит» у соседних шаблонов.
          Простой строчкой на белом она читалась как обрывок текста письма,
          а не как пояснение к полю.
        */}
        <p
          className="mt-2.5 rounded-lg border px-3 py-2 text-xs leading-relaxed text-ink2"
          style={{ borderColor: 'var(--otc-line)', background: 'var(--otc-bg)' }}
        >
          Уйдёт клинике как есть — подстановок здесь нет. Очистите поле и сохраните,
          чтобы вернуть текст по умолчанию.
        </p>
      </div>

      <p className="text-xs text-ink2">
        Письма уходят по нажатию — сами по себе никогда. Уведомление врачу предлагается при
        заведении заказа и при переводе в «Готово», письмо со счётом — из карточки счёта.
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------

function ReportsTab({
  show,
  масштаб = 100,
}: {
  show: (text: string, tone?: 'info' | 'error') => void;
  /** Размер СТРОК списка. Отборы и заголовки от него не зависят. */
  масштаб?: number;
}) {
  const reports = useReports(true);
  const [openId, setOpenId] = useState<string | null>(null);
  const [confirmId, setConfirmId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  if (reports.isLoading) return <Spinner />;
  const list = reports.data?.reports ?? [];

  const open = list.find((r) => r.id === openId) ?? null;
  const toDelete = list.find((r) => r.id === confirmId) ?? null;

  const remove = async (report: ApiReport) => {
    setBusy(true);
    try {
      await api.del(`/api/reports/${report.id}`);
      show(`Наряд «${report.clinicName}» удалён`);
      setConfirmId(null);
      setOpenId(null);
      void reports.refetch();
    } catch (error) {
      show(error instanceof Error ? error.message : 'Не удалось удалить', 'error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-2" style={{ zoom: `${масштаб}%` }}>
      {list.map((report) => (
        /*
          На ТЕЛЕФОНЕ строка отчёта раскладывается СВЕРХУ ВНИЗ.

          Прежде все части — название, сумма, три кнопки и корзина — стояли
          одним переносящимся рядом, и перенос выпадал по-разному у каждого
          отчёта: у одного «Excel» с корзиной уезжали на вторую строку,
          у другого — только корзина. Список выглядел кривым, хотя строки
          одинаковые.
        */
        <div
          key={report.id}
          className="rounded-xl border p-4 md:flex md:flex-wrap md:items-center md:gap-3"
          style={{ borderColor: 'var(--otc-line)', background: 'var(--otc-surface)' }}
        >
          {/*
            Наряд открывается щелчком по названию — как счёт в соседней вкладке.
            Раньше сохранённый наряд можно было только скачать: посмотреть, что
            в нём, и удалить лишний было нечем.
          */}
          {/* Как в счетах: на телефоне заголовок занимает целую строку. */}
          <button
            onClick={() => setOpenId(report.id)}
            className="w-full min-w-0 flex-1 basis-full text-left md:w-auto md:basis-0"
          >
            <p className="text-sm font-medium text-ink">{report.clinicName}</p>
            <p className="text-xs text-ink2">
              {report.periodLabel} · {report.rows.length}{' '}
              {pluralRu(report.rows.length, ['строка', 'строки', 'строк'])} · сохранён{' '}
              {formatDate(report.date)}
              {report.responsible && ` · ${report.responsible}`}
            </p>
          </button>
          <span className="tabular mt-1 block text-sm text-ink md:mt-0">
            {formatKop(report.totalKop)} ₽
          </span>

          {/*
            Действия — ОДНИМ ровным рядом в четыре равные доли. На широком
            экране обёртка исчезает (`md:contents`), и кнопки встают в общую
            строку карточки, как раньше.
          */}
          <div className="mt-2.5 grid grid-cols-4 items-center gap-2 md:mt-0 md:contents">
            <Button variant="secondary" onClick={() => setOpenId(report.id)}>
              <Eye size={14} /> Открыть
            </Button>
            <a href={reportExportUrl(report.id, 'pdf')} className="contents">
              <Button variant="secondary">
                <Download size={14} /> PDF
              </Button>
            </a>
            <a href={reportExportUrl(report.id)} className="contents">
              <Button variant="secondary">
                <Download size={14} /> Excel
              </Button>
            </a>
            <button
              onClick={() => setConfirmId(report.id)}
              className="flex items-center justify-center rounded-lg border px-3 py-2 text-sm text-ink2 transition-colors hover:border-[color:var(--otc-coral)] hover:text-[color:var(--otc-coral)]"
              style={{ borderColor: 'var(--otc-line)' }}
              title="Удалить отчёт"
            >
              <Trash2 size={14} />
            </button>
          </div>
        </div>
      ))}

      {list.length === 0 && (
        <EmptyState text="Сохранённых отчётов пока нет. Отчёт формируется в разделе «Заказы»." />
      )}

      <p className="text-xs text-ink2">
        Отчёт сохраняется снимком: он не изменится, если заказы отредактируют позже. Поправить сам отчёт можно в нём: «Открыть» → «Редактировать».
      </p>

      {open && (
        <ReportViewModal
          report={open}
          onClose={() => setOpenId(null)}
          onSaved={(message) => {
            show(message);
            void reports.refetch();
          }}
          onError={(message) => show(message, 'error')}
          onNotify={(message) => show(message)}
        />
      )}

      {toDelete && (
        <ConfirmDialog
          title={`Удалить отчёт «${toDelete.clinicName}»?`}
          description="Сохранённый снимок исчезнет из истории. Заказы и счета это не затронет."
          details={
            <p>
              {toDelete.periodLabel} · строк: {toDelete.rows.length} ·{' '}
              {formatKop(toDelete.totalKop)} ₽
            </p>
          }
          confirmLabel={busy ? 'Удаление…' : 'Удалить отчёт'}
          onConfirm={() => void remove(toDelete)}
          onCancel={() => setConfirmId(null)}
        />
      )}
    </div>
  );
}

/**
 * Кнопка панели действий над отмеченными счетами.
 *
 * Значок и подпись В СТРОКУ, а не столбиком: подписи здесь длинные
 * («Скачать архив Word»), и поставленные под значок они растянули бы панель
 * втрое. На телефоне подпись короткая — там строка в дефиците.
 */
function BarAction({
  icon: Icon,
  full,
  short,
  color,
  disabled,
  onClick,
}: {
  icon: (props: { size?: number | string }) => ReactNode;
  full: string;
  short: string;
  color: string;
  disabled?: boolean;
  onClick: () => void;
}) {
  /*
    Тот же кусочек, что в панели выделения «Заказов»: значок над короткой
    подписью, крупнее и с тем же откликом на нажатие. Полная подпись уходит
    в подсказку под курсором — под значком она не поместилась бы, а в двух
    словах «Скачать архив Word» и так узнаётся по значку загрузки.
  */
  return (
    <SelectionActionButton
      icon={Icon}
      label={short}
      color={color}
      disabled={disabled}
      title={full}
      onClick={onClick}
    />
  );
}

/**
 * Сохранённые прайс-листы — листы, поправленные в окне прайса.
 *
 * Открываются тем же окном, что в «Конструкциях»: там их правят, печатают и
 * сохраняют снова. Хранятся снимком — правки справочника их не меняют.
 */
function PriceListsTab({
  show,
  масштаб = 100,
}: {
  show: (text: string, tone?: 'info' | 'error') => void;
  /** Размер СТРОК списка. Отборы и заголовки от него не зависят. */
  масштаб?: number;
}) {
  const priceLists = usePriceLists(true);
  const [openId, setOpenId] = useState<string | null>(null);
  const [confirmId, setConfirmId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  if (priceLists.isLoading) return <Spinner />;
  const list = priceLists.data?.priceLists ?? [];
  const open = list.find((item) => item.id === openId) ?? null;
  const toDelete = list.find((item) => item.id === confirmId) ?? null;

  const rowsOf = (item: ApiPriceList) =>
    item.model.parts.reduce((sum, part) => sum + part.sections.reduce((s, section) => s + section.rows.length, 0), 0);

  const remove = async (item: ApiPriceList) => {
    setBusy(true);
    try {
      await api.del(`/api/price-lists/${item.id}`);
      show(`«${item.name}» удалён`);
      setConfirmId(null);
      setOpenId(null);
      void priceLists.refetch();
    } catch (error) {
      show(error instanceof Error ? error.message : 'Не удалось удалить', 'error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-2" style={{ zoom: `${масштаб}%` }}>
      {list.map((item) => (
        <div
          key={item.id}
          className="flex flex-wrap items-center gap-3 rounded-xl border p-4"
          style={{ borderColor: 'var(--otc-line)', background: 'var(--otc-surface)' }}
        >
          <button onClick={() => setOpenId(item.id)} className="min-w-0 flex-1 basis-full text-left md:basis-0">
            <p className="text-sm font-medium text-ink">{item.name}</p>
            <p className="text-xs text-ink2">
              {rowsOf(item)} {pluralRu(rowsOf(item), ['позиция', 'позиции', 'позиций'])} · изменён{' '}
              {formatDate(item.updatedAt.slice(0, 10))}
            </p>
          </button>
          <Button variant="secondary" onClick={() => setOpenId(item.id)}>
            <Eye size={14} /> Открыть
          </Button>
          <a href={`/api/price-lists/${item.id}/export.pdf`}>
            <Button variant="secondary">
              <Download size={14} /> PDF
            </Button>
          </a>
          <button
            onClick={() => setConfirmId(item.id)}
            className="rounded-lg border px-3 py-2 text-sm text-ink2 transition-colors hover:border-[color:var(--otc-coral)] hover:text-[color:var(--otc-coral)]"
            style={{ borderColor: 'var(--otc-line)' }}
            title="Удалить прайс-лист"
          >
            <Trash2 size={14} />
          </button>
        </div>
      ))}

      {list.length === 0 && (
        <EmptyState text="Сохранённых прайс-листов пока нет. Прайс открывается в «Конструкциях» кнопкой «Прайс-лист» — там его можно поправить и сохранить сюда." />
      )}

      <p className="text-xs text-ink2">
        Прайс-лист сохраняется снимком: правки справочника его не меняют, а его правки не меняют цены в заказах.
      </p>

      {open && (
        <PriceListModal
          saved={{ id: open.id, model: open.model }}
          onClose={() => setOpenId(null)}
          onSaved={(message) => {
            show(message);
            void priceLists.refetch();
          }}
          onError={(message) => show(message, 'error')}
        />
      )}

      {toDelete && (
        <ConfirmDialog
          title={`Удалить «${toDelete.name}»?`}
          description="Сохранённый прайс-лист исчезнет из документов. Справочник конструкций и цены в заказах это не затронет."
          details={<p>{rowsOf(toDelete)} {pluralRu(rowsOf(toDelete), ['позиция', 'позиции', 'позиций'])}</p>}
          confirmLabel={busy ? 'Удаление…' : 'Удалить прайс-лист'}
          onConfirm={() => void remove(toDelete)}
          onCancel={() => setConfirmId(null)}
        />
      )}
    </div>
  );
}
